/**
 * Rules / scoring info sheet.
 *
 * Why this exists: a real player (the developer himself) could not figure out
 * the core one-stroke rule — "why can a line sometimes repeat?" The confusion
 * is line (edge) vs dot (vertex): an EDGE is drawn exactly once and can never be
 * redrawn, but a VERTEX can be passed through many times. The game never said so,
 * and the leaderboard "?" (data-scoring) was a dead no-op stub.
 *
 * This is an OPTIONAL, player-triggered explanation — it never blocks play and is
 * not a forced tutorial, so it honours I-14 (no forced text-only onboarding).
 * Visually it reuses the austere freeze-info-* bottom-sheet (paper bg, serif
 * italic title, single sacred gold button) — no new visual language, I-20 safe.
 *
 * Content is fact-derived from code, not invented:
 *  - rule: play-engine.ts (traversed edge is rejected; vertices reused)
 *  - rating: worker/src/rating.ts (time vs daily median × rarity mult, Elo)
 *  - country: worker/src/index.ts (10 pts/solve, weekly, Monday-UTC reset)
 *  - stars: engine/chapters.ts starsForSolve(mistakes) — 0 mistakes = ★★★,
 *           1–2 = ★★, 3+ = ★ (Practice/World-Cities only; a "mistake" is a
 *           drag toward a dot with no available line; TIME does not affect stars).
 */
import { t } from '../i18n/runtime';
import { el, html, on } from './html';

/**
 * Mount the rules sheet into `host` as an overlay. Returns a disposer that
 * removes it. The scrim, the close button, and Escape all dismiss it.
 */
export function openRulesSheet(host: HTMLElement): () => void {
  const overlay = el(html`
    <div class="screen-freeze-info rules-overlay" role="dialog" aria-modal="true" aria-label="${t('rules.title')}">
      <div class="freeze-info-scrim" data-rules-close aria-hidden="true"></div>
      <div class="freeze-info-sheet rules-sheet">
        <div class="freeze-info-handle" aria-hidden="true"></div>
        <p class="rules-goal">${t('rules.goal')}</p>
        <h2 class="freeze-info-title">${t('rules.title')}</h2>
        <p class="freeze-info-body">${t('rules.body')}</p>
        <div class="rules-legend">
          <div class="rules-leg-cap">${t('rules.legend_cap')}</div>
          <div class="rules-leg-grid">
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="34" height="14" viewBox="0 0 34 14"><line x1="3" y1="7" x2="31" y2="7" stroke="var(--color-gold)" stroke-width="5" stroke-linecap="round"/></svg></span><span class="rules-leg-l">${t('rules.legend_drawn')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="34" height="14" viewBox="0 0 34 14"><line x1="3" y1="7" x2="31" y2="7" stroke="var(--color-ink-soft)" stroke-width="1.6" stroke-linecap="round"/></svg></span><span class="rules-leg-l">${t('rules.legend_not_yet')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="6.5" fill="var(--color-ink)"/></svg></span><span class="rules-leg-l">${t('rules.legend_has_lines')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="5.5" fill="var(--color-ink-soft)"/></svg></span><span class="rules-leg-l">${t('rules.legend_done')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="6.5" fill="var(--color-gold)"/></svg></span><span class="rules-leg-l">${t('rules.legend_your_pos')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-sw"><svg width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" fill="none" stroke="var(--color-gold)" stroke-width="1.8"/><circle cx="10" cy="10" r="4" fill="var(--color-ink)"/></svg></span><span class="rules-leg-l">${t('rules.legend_start')}</span></div>
          </div>
        </div>
        <h2 class="freeze-info-title">${t('rules.scoring_title')}</h2>
        <p class="freeze-info-body">${t('rules.scoring_body')}</p>
        <div class="rules-legend rules-stars">
          <div class="rules-leg-cap">${t('rules.stars_title')}</div>
          <div class="rules-leg-grid">
            <div class="rules-leg-row"><span class="rules-leg-l">${t('rules.stars_3')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-l">${t('rules.stars_2')}</span></div>
            <div class="rules-leg-row"><span class="rules-leg-l">${t('rules.stars_1')}</span></div>
          </div>
        </div>
        <p class="freeze-info-body rules-stars-note">${t('rules.stars_note')}</p>
        <button class="freeze-info-close" type="button" data-rules-close>${t('rules.got_it')}</button>
      </div>
    </div>
  `);

  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    document.removeEventListener('keydown', onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') dispose();
  };

  on(overlay, 'click', '[data-rules-close]', () => dispose());
  document.addEventListener('keydown', onKey);
  host.appendChild(overlay);

  // Move focus to the dismiss button for keyboard / screen-reader users.
  overlay.querySelector<HTMLButtonElement>('.freeze-info-close')?.focus();
  return dispose;
}
